/**
 * Friends, blocks and DMs. Every action resolves with whether it worked, so
 * a caller never shows a success after a failure (the failure has its own
 * toast here). Block and Remove friend ask first (user-management §3.8).
 */
import { ChannelType, normalizeUsername, type Channel, type Snowflake } from '@neosocial/protocol';
import { tr } from '../i18n';
import { api } from '../lib/api';
import { updateData, useData } from '../store/data';
import { goHub, useUi } from '../store/ui';
import { useUm } from '../store/um';
import { confirmDialog, toast } from '../ui/overlay';
import { errorText } from '../mod/actions';

function fail(e: unknown): void {
  // the same wording as everywhere, a staff limit's end date included
  toast(errorText(e), { tone: 'danger', icon: 'alert' });
}

async function attempt(fn: () => Promise<unknown>): Promise<boolean> {
  try {
    await fn();
    return true;
  } catch (e) {
    fail(e);
    return false;
  }
}

/** Puts a DM the API returned into the store; `requestState` replaces the one it had. */
function storeChannel(c: Channel, requestState?: Channel['nx_request_state']): void {
  updateData((s) => ({
    ...s,
    channels: {
      ...s.channels,
      [c.id]: { ...s.channels[c.id], ...c, nx_request_state: requestState },
    },
  }));
}

/**
 * Opens (or creates) the 1:1 DM with someone and shows it. One you
 * dismissed as a message request asks the server, which brings it back to
 * your DM list (opening it yourself means you want to talk).
 */
export async function openDm(userId: Snowflake): Promise<void> {
  const d = useData.getState();
  const existing = Object.values(d.channels).find(
    (c) => c.type === ChannelType.DM && c.recipients?.some((u) => u.id === userId),
  );
  let id = existing?.id;
  if (!id || existing?.nx_request_state === 'dismissed') {
    try {
      const c = await api.me.openPrivateChannel({ recipient_id: userId });
      storeChannel(
        c,
        existing?.nx_request_state === 'dismissed' ? 'accepted' : existing?.nx_request_state,
      );
      id = c.id;
    } catch (e) {
      fail(e);
      return;
    }
  }
  goHub({ kind: 'home', channelId: id });
}

export async function createGroupDm(userIds: Snowflake[]): Promise<void> {
  try {
    const c = await api.me.openPrivateChannel({ recipients: userIds });
    storeChannel(c);
    goHub({ kind: 'home', channelId: c.id });
  } catch (e) {
    fail(e);
  }
}

/** A request to your own username says so (it is about you, not them). */
export async function sendFriendRequest(username: string): Promise<'sent' | 'error'> {
  const name = username.trim().replace(/^@/, '');
  const me = useData.getState().me;
  if (me && normalizeUsername(name) === normalizeUsername(me.username)) {
    toast(tr().t('social.fr.self'), { icon: 'info' });
    return 'error';
  }
  try {
    await api.me.sendFriendRequest({ username: name });
    return 'sent';
  } catch (e) {
    fail(e);
    return 'error';
  }
}

export function acceptFriend(userId: Snowflake): Promise<boolean> {
  return attempt(() => api.me.putRelationship(userId));
}

export function removeRelationship(userId: Snowflake): Promise<boolean> {
  return attempt(() => api.me.deleteRelationship(userId));
}

export function blockUser(userId: Snowflake): Promise<boolean> {
  return attempt(() => api.me.putRelationship(userId, 2));
}

/** Accept, then "You and {name} are now friends" only when it worked. */
export async function acceptFriendWithToast(userId: Snowflake, name: string): Promise<boolean> {
  const ok = await acceptFriend(userId);
  if (ok) toast(tr().t('hubv.fr.accepted', { name }), { icon: 'user-plus', tone: 'ok' });
  return ok;
}

/** The spec's Block sheet (`block.confirm.*`), then the block and its toast. */
export async function confirmBlock(userId: Snowflake, name: string): Promise<boolean> {
  const { t } = tr();
  const yes = await confirmDialog({
    title: t('block.confirm.title', { name }),
    body: t('block.confirm.body'),
    confirmLabel: t('ns.fr.block'),
    danger: true,
    icon: 'shield',
  });
  if (!yes) return false;
  const ok = await blockUser(userId);
  if (ok) toast(t('social.block.done', { name }), { icon: 'shield' });
  return ok;
}

export async function unblock(userId: Snowflake, name: string): Promise<boolean> {
  const ok = await removeRelationship(userId);
  if (ok) toast(tr().t('social.block.undone', { name }), { icon: 'shield' });
  return ok;
}

/** "Remove {name} from your friends? They won't be told." (`friends.remove`), then the toast. */
export async function confirmRemoveFriend(userId: Snowflake, name: string): Promise<boolean> {
  const { t } = tr();
  const yes = await confirmDialog({
    title: t('friends.remove', { name }),
    confirmLabel: t('hubv.fr.remove'),
    danger: true,
    icon: 'x',
  });
  if (!yes) return false;
  const ok = await removeRelationship(userId);
  if (ok) toast(t('hubv.fr.removed', { name }), { icon: 'x' });
  return ok;
}

/** Leaves the conversation's screen when it is the one open. */
function leaveView(channelId: Snowflake): void {
  const v = useUi.getState().hub;
  if (v?.kind === 'home' && v.channelId === channelId) goHub({ kind: 'home' });
}

function dropChannel(channelId: Snowflake): void {
  updateData((s) => {
    const channels = { ...s.channels };
    delete channels[channelId];
    return { ...s, channels };
  });
}

/** Close DM (§3.3: hides it; their next message or a new DM with them brings it back). */
export async function closeDm(channelId: Snowflake): Promise<boolean> {
  const ok = await attempt(() => api.channels.delete(channelId));
  if (ok) {
    leaveView(channelId);
    dropChannel(channelId);
  }
  return ok;
}

/** Leave group, after asking (a member who leaves needs re-adding to come back). */
export async function leaveGroup(channelId: Snowflake, name: string): Promise<boolean> {
  const { t } = tr();
  const yes = await confirmDialog({
    title: t('social.gdm.leaveTitle', { name }),
    body: t('social.gdm.leaveBody'),
    confirmLabel: t('gdm.leave'),
    danger: true,
    icon: 'arrow-out',
  });
  if (!yes) return false;
  const ok = await attempt(() => api.channels.delete(channelId));
  if (ok) {
    leaveView(channelId);
    dropChannel(channelId);
    toast(t('social.gdm.left', { name }), { icon: 'arrow-out' });
  }
  return ok;
}

/** Renames a group DM; an empty name shows the members' names again. */
export async function renameGroup(channelId: Snowflake, name: string): Promise<boolean> {
  const value = name.trim();
  try {
    const c = await api.channels.modify(channelId, { name: value || null });
    storeChannel(c);
    toast(tr().t('social.gdm.renamed'), { icon: 'check', tone: 'ok' });
    return true;
  } catch (e) {
    fail(e);
    return false;
  }
}

/** Adds friends to a group DM, one by one; stops at the first refusal. */
export async function addToGroup(channelId: Snowflake, userIds: Snowflake[]): Promise<boolean> {
  let added = 0;
  for (const id of userIds) {
    if (!(await attempt(() => api.channels.addRecipient(channelId, id)))) break;
    added += 1;
  }
  if (added) toast(tr().t('social.gdm.added', { n: added }), { icon: 'user-plus', tone: 'ok' });
  return added === userIds.length;
}

/** The owner removes someone from the group. */
export async function removeFromGroup(
  channelId: Snowflake,
  userId: Snowflake,
  name: string,
): Promise<boolean> {
  const ok = await attempt(() => api.channels.removeRecipient(channelId, userId));
  if (ok) toast(tr().t('social.gdm.removed', { name }), { icon: 'x' });
  return ok;
}

/** The owner hands the group over. */
export async function makeGroupOwner(
  channelId: Snowflake,
  userId: Snowflake,
  name: string,
): Promise<boolean> {
  try {
    const c = await api.channels.modify(channelId, { owner_id: userId });
    storeChannel(c);
    toast(tr().t('social.gdm.ownerNow', { name }), { icon: 'crown', tone: 'ok' });
    return true;
  } catch (e) {
    fail(e);
    return false;
  }
}

/** True when the signed-in user ignores `userId` (for views that grey them out). */
export function useIsIgnored(userId: Snowflake | undefined): boolean {
  return useUm((s) => !!userId && !!s.ignored[userId]);
}

export { fail as reportError };
